import React, { useState, useEffect } from 'react';
import { Mail, Trash2, Check, RefreshCw, MessageCircle, Clock, ExternalLink, ChevronDown, ChevronUp, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateShort, formatDateTime } from '../utils/dateUtils';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SUJETS_COLORS = {
  'Demande de devis': { bg: 'rgba(212,175,55,0.12)', color: 'var(--admin-amber)' },
  'Mariage': { bg: 'rgba(244,167,178,0.12)', color: '#B94B5C' },
  'Anniversaire enfant': { bg: 'rgba(244,167,178,0.12)', color: '#B94B5C' },
  'Entreprise & CE': { bg: 'rgba(93,188,191,0.12)', color: '#0B4B4E' },
  'Question sur un produit': { bg: 'rgba(93,188,191,0.12)', color: '#0B4B4E' },
  'default': { bg: 'rgba(197,139,25,0.1)', color: 'var(--admin-amber)' },
};

function getSubjectStyle(sujet) {
  return SUJETS_COLORS[sujet] || SUJETS_COLORS['default'];
}

function timeAgo(isoDate) {
  const now = new Date();
  const date = new Date(isoDate);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;
  return formatDateShort(date);
}

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [nonLus, setNonLus] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState('all'); // 'all' | 'unread' | 'read'
  const [expanded, setExpanded] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    try {
      const token = getToken();
      const lu = filtre === 'unread' ? 'false' : filtre === 'read' ? 'true' : undefined;
      const url = lu !== undefined
        ? `${BACKEND}/api/messages?lu=${lu}&limit=100`
        : `${BACKEND}/api/messages?limit=100`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMessages(data.messages || []);
      setNonLus(data.total_non_lus || 0);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [filtre]); // eslint-disable-line

  const marquerLu = async (id) => {
    try {
      const token = getToken();
      await fetch(`${BACKEND}/api/messages/${id}/lu`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, lu: true } : m));
      setNonLus(prev => Math.max(0, prev - 1));
    } catch {
      toast.error('Erreur');
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      const token = getToken();
      await fetch(`${BACKEND}/api/messages/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => prev.filter(m => m.id !== id));
      toast.success('Message supprimé');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    const msg = messages.find(m => m.id === id);
    if (msg && !msg.lu) await marquerLu(id);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Messages clients</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Messages recus via le site</p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(212,175,55,0.12)' }}>
              <Inbox size={20} style={{ color: 'var(--admin-gold)' }} />
            </div>
            <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>
              Messagerie
            </h1>
            {nonLus > 0 && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold"
                style={{ background: '#D4AF37', color: '#0B1E20' }}>
                {nonLus} non lu{nonLus > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>
            Messages reçus depuis le formulaire de contact du site
          </p>
        </div>
        <button
          onClick={() => load(false)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
          style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
          <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-5">
        {[
          { key: 'all', label: 'Tous' },
          { key: 'unread', label: `Non lus${nonLus > 0 ? ` (${nonLus})` : ''}` },
          { key: 'read', label: 'Lus' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFiltre(f.key)}
            className="px-4 py-2 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: filtre === f.key ? '#D4AF37' : 'var(--bg)',
              color: filtre === f.key ? '#0B1E20' : 'var(--text-muted)',
              border: `1px solid ${filtre === f.key ? '#D4AF37' : 'var(--border)'}`,
              cursor: 'pointer',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 rounded-2xl animate-pulse"
              style={{ background: 'var(--border)' }} />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-16">
          <MessageCircle size={40} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="font-semibold text-sm" style={{ color: 'var(--admin-muted)' }}>
            Aucun message {filtre === 'unread' ? 'non lu' : ''}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--admin-muted)' }}>
            Les messages du formulaire de contact apparaîtront ici
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map(msg => {
            const isOpen = expanded === msg.id;
            const subjectStyle = getSubjectStyle(msg.sujet);
            return (
              <div
                key={msg.id}
                className="rounded-2xl overflow-hidden transition-all"
                style={{
                  border: `1px solid ${!msg.lu ? 'rgba(212,175,55,0.4)' : 'var(--border)'}`,
                  background: !msg.lu ? 'rgba(212,175,55,0.03)' : '#FFFFFF',
                  boxShadow: !msg.lu ? '0 2px 12px rgba(212,175,55,0.08)' : 'none',
                }}>
                {/* En-tête du message */}
                <button
                  onClick={() => handleExpand(msg.id)}
                  className="w-full flex items-center gap-4 p-4 text-left"
                  style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
                    style={{ background: subjectStyle.bg, color: subjectStyle.color }}>
                    {msg.nom?.charAt(0)?.toUpperCase() || 'U'}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>
                        {msg.nom}
                      </span>
                      {!msg.lu && (
                        <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: '#D4AF37' }} />
                      )}
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ background: subjectStyle.bg, color: subjectStyle.color }}>
                        {msg.sujet}
                      </span>
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
                      {msg.message}
                    </p>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                        <Clock size={10} className="inline mr-1" />
                        {timeAgo(msg.created_at)}
                      </p>
                      {msg.page_source && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--admin-muted)', opacity: 0.6 }}>
                          {msg.page_source}
                        </p>
                      )}
                    </div>
                    {isOpen ? (
                      <ChevronUp size={16} style={{ color: 'var(--admin-muted)' }} />
                    ) : (
                      <ChevronDown size={16} style={{ color: 'var(--admin-muted)' }} />
                    )}
                  </div>
                </button>

                {/* Contenu développé */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--admin-border)' }}>
                    <div className="pt-4 grid sm:grid-cols-2 gap-3 mb-4">
                      <div className="p-3 rounded-xl" style={{ background: 'var(--admin-bg)' }}>
                        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--admin-muted)' }}>Email</p>
                        <a href={`mailto:${msg.email}`}
                          className="text-sm font-medium flex items-center gap-1 hover:underline"
                          style={{ color: 'var(--admin-teal)' }}>
                          <Mail size={13} /> {msg.email}
                        </a>
                      </div>
                      <div className="p-3 rounded-xl" style={{ background: 'var(--admin-bg)' }}>
                        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--admin-muted)' }}>Reçu le</p>
                        <p className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>
                          {formatDateTime(msg.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Message complet */}
                    <div className="p-4 rounded-xl mb-4"
                      style={{ background: 'var(--admin-bg)', borderLeft: '3px solid #D4AF37' }}>
                      <p className="text-sm" style={{ color: 'var(--admin-text)', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                        {msg.message}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={`mailto:${msg.email}?subject=Re: ${encodeURIComponent(msg.sujet)}&body=Bonjour ${encodeURIComponent(msg.nom)},%0A%0A`}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold"
                        style={{ background: '#D4AF37', color: '#0B1E20', textDecoration: 'none' }}>
                        <Mail size={13} /> Répondre par email
                      </a>
                      {!msg.lu && (
                        <button
                          onClick={() => marquerLu(msg.id)}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold"
                          style={{ background: 'var(--admin-green-dim)', color: 'var(--admin-green)', border: '1px solid rgba(31,157,122,0.2)', cursor: 'pointer' }}>
                          <Check size={13} /> Marquer comme lu
                        </button>
                      )}
                      <button
                        onClick={() => supprimer(msg.id)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold ml-auto"
                        style={{ background: 'rgba(239,68,68,0.08)', color: '#DC2626', border: '1px solid rgba(239,68,68,0.15)', cursor: 'pointer' }}>
                        <Trash2 size={13} /> Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
